import { createSelector as createMemoizedSelector, Selector } from 'reselect';

import { ContentFiltersKeys } from '../../ts/models/ContentFilters';
import { getKeyForExplorableItem, SpaceInfoItemType } from '../../ts/models/ContentType';
import { IExplorableItemTypeInfo, ISpaceTypeInfo } from '../../ts/models/resourceInfoParts';
import { doesSpaceSupportStylishFiltering } from '../actions/spaces/filters/FilterHelpers';
import { ItemsSet, makeItemsSet } from '../ItemsSet';
import { AppState } from '../state/AppState';
import { isFavoritesSpace, isRecommendationsSpace, SpaceInfo } from '../state/SpaceInfo';
import { selectSpecificMixedContentFilterClause } from './ContentFilterSelectors';
import { selectRecommendationType } from './RecommendationTypeSelector';

export function selectSpaceInfo(state: AppState): SpaceInfo {
  return state.spaceInfo;
}

export function selectSpaceInfoItem(state: AppState): ISpaceTypeInfo {
    return state.spaceInfo.item;
}

export function selectContentFromAppState(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  const currSpaceInfo = selectSpaceInfo(state);
  const favoriteFilterApplied = selectSpecificMixedContentFilterClause(state, ContentFiltersKeys.favorite);
  const removeFavoriteFromExplorer = favoriteFilterApplied && doesSpaceSupportStylishFiltering(currSpaceInfo);

  if (isFavoritesSpace(currSpaceInfo) || removeFavoriteFromExplorer) {
    return selectFavoritedContentItems(state);
  } else if (isRecommendationsSpace(currSpaceInfo)) {
    return filterByRecommendationType(state);
  } else {
    return state.contentExplorer.content.contentItems;
  }
}

export function isInCurrentSpace(state: AppState, space: {spaceType: SpaceInfoItemType, id: string }) {
  const currSpaceInfo = selectSpaceInfo(state);
  return currSpaceInfo.item.id === space.id && currSpaceInfo.spaceType === space.spaceType;
}

export function selectContentTotalLength(state: AppState): number {
  return state.contentExplorer.content.contentItems.length;
}

export function selectContentExplorerSelectionIsBusy(state: AppState): boolean {
  return state.contentExplorer.selection.isBusy;
}


function selectAllSelectedContentItems(state: AppState): ItemsSet<IExplorableItemTypeInfo> {
  return state.contentExplorer.selection.selectedItems;
}

function filterByRecommendationType(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  const unhiddenItems = state.contentExplorer.content.contentItems
  .filter((item) => (item as any).hide === false);

  if (selectRecommendationType(state) !== 'all') {
    return unhiddenItems
    .filter((item) => (item as any).explanationType === selectRecommendationType(state));
  }

  return unhiddenItems;
}

/**
 * Filters out the selected items with respect to the actual content items, to make sure
 * we don't still select items that are no longer in our list.
 *
 * The actions menu from the explorer toolbar takes in the selected items set as its target items,
 * rather than the actual items from the content explorer that correspond to these selected keys.
 * This can be removed when we update all sagas to use IContentTypeItemInfo instead of IContentInfo
 * (see TFS tech debt story 808815).
 */
export const selectSelectedContentItems: Selector<AppState, ItemsSet<IExplorableItemTypeInfo>> =
  createMemoizedSelector([selectContentFromAppState, selectAllSelectedContentItems],
    (contentItems, allSelectedContentItems) => {
      const filteredSelectedItems = contentItems.filter(c => {
        const key = getKeyForExplorableItem(c);
        return allSelectedContentItems.hasOwnProperty(key);
      });
      return makeItemsSet(getKeyForExplorableItem, ...filteredSelectedItems);
    });

function selectAllContentItems(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  return state.contentExplorer.content.contentItems;
}
export const selectFavoritedContentItems: Selector<AppState, ReadonlyArray<IExplorableItemTypeInfo>> =
  createMemoizedSelector([selectAllContentItems], (items) => {
    return items.filter((item) => (item as any).favorite);
  });
